import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Accessibility,
  X,
  ZoomIn,
  Contrast,
  Link2,
  Type,
  Droplet,
  RotateCcw,
  FileText,
} from "lucide-react";

const OPTIONS = [
  { key: "a11y-lg", label: "Bigger Text", icon: ZoomIn, group: "text" },
  { key: "a11y-xl", label: "Largest Text", icon: Type, group: "text" },
  { key: "a11y-spacing", label: "Readable Spacing", icon: Type, group: "spacing" },
  { key: "a11y-contrast", label: "High Contrast", icon: Contrast, group: "filter" },
  { key: "a11y-grayscale", label: "Grayscale", icon: Droplet, group: "filter" },
  { key: "a11y-links", label: "Highlight Links", icon: Link2, group: "links" },
];

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState([]);

  useEffect(() => {
    const root = document.documentElement;
    OPTIONS.forEach((o) => root.classList.remove(o.key));
    active.forEach((k) => root.classList.add(k));
  }, [active]);

  const toggle = (opt) => {
    setActive((prev) => {
      // text size options are mutually exclusive
      if (opt.group === "text") {
        const withoutText = prev.filter(
          (k) => !OPTIONS.some((o) => o.group === "text" && o.key === k)
        );
        return prev.includes(opt.key) ? withoutText : [...withoutText, opt.key];
      }
      if (opt.group === "filter") {
        const withoutFilter = prev.filter(
          (k) => !OPTIONS.some((o) => o.group === "filter" && o.key === k)
        );
        return prev.includes(opt.key) ? withoutFilter : [...withoutFilter, opt.key];
      }
      return prev.includes(opt.key)
        ? prev.filter((k) => k !== opt.key)
        : [...prev, opt.key];
    });
  };

  const reset = () => setActive([]);

  return (
    <>
      <button
        type="button"
        aria-label={open ? "Close accessibility menu" : "Open accessibility menu"}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-24 left-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-[#0b5cd6] text-white shadow-xl ring-2 ring-white transition-transform hover:scale-105 focus-visible:scale-105 lg:bottom-5"
      >
        {open ? <X className="h-7 w-7" aria-hidden="true" /> : <Accessibility className="h-7 w-7" aria-hidden="true" />}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Accessibility options"
          className="fixed bottom-44 left-5 z-[60] w-[320px] max-w-[calc(100vw-2.5rem)] rounded-xl border border-neutral-200 bg-white p-5 shadow-2xl lg:bottom-24"
        >
          <div className="mb-3 flex items-center gap-2">
            <Accessibility className="h-5 w-5 text-[#0b5cd6]" aria-hidden="true" />
            <h2 className="text-base font-bold uppercase tracking-wide text-neutral-900">
              Accessibility Tools
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isOn = active.includes(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  aria-pressed={isOn}
                  onClick={() => toggle(opt)}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-center text-xs font-semibold transition-colors ${
                    isOn
                      ? "border-[#0b5cd6] bg-[#0b5cd6] text-white"
                      : "border-neutral-200 bg-neutral-50 text-neutral-700 hover:border-[#0b5cd6]"
                  }`}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                  {opt.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={reset}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-100"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset
          </button>
          <Link
            to="/accessibility-statement"
            onClick={() => setOpen(false)}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-neutral-900 py-2 text-sm font-semibold text-white hover:bg-neutral-800"
          >
            <FileText className="h-4 w-4" aria-hidden="true" /> Accessibility Statement
          </Link>
        </div>
      )}
    </>
  );
}
